/**
 * WhatsApp utility functions
 * Opens WhatsApp with phone number and pre-filled message
 */

/**
 * Format phone number for WhatsApp (remove all non-digits)
 * @param {string} phone - Phone number in any format
 * @returns {string} - Cleaned phone number with only digits
 */
export const formatPhoneForWhatsApp = (phone) => {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  return digits || null;
};

/**
 * Open WhatsApp with phone number and message
 * @param {string} phone - Phone number
 * @param {string} message - Pre-filled message
 * @returns {boolean} - True if opened successfully
 */
export const openWhatsApp = (phone, message = '') => {
  if (!phone) {
    return false;
  }

  const formattedPhone = formatPhoneForWhatsApp(phone);
  if (!formattedPhone) {
    return false;
  }

  const encodedMessage = encodeURIComponent(message);
  const url = `https://wa.me/${formattedPhone}${message ? `?text=${encodedMessage}` : ''}`;

  if (typeof window !== 'undefined') {
    window.open(url, '_blank');
    return true;
  }

  return false;
};

/**
 * Open WhatsApp for multiple contacts (opens one at a time)
 * @param {Array<{phone: string, name?: string}>} contacts - Array of contacts with phone numbers
 * @param {string} message - Pre-filled message
 * @returns {Object} - { opened: number, failed: number, details: Array }
 */
export const openWhatsAppForMultiple = (contacts, message = '') => {
  let opened = 0;
  let failed = 0;
  const details = [];

  contacts.forEach((contact, index) => {
    const phone = contact.phone || contact.phoneNumber;
    const name = contact.name || contact.guestName || `Guest ${index + 1}`;

    if (!phone) {
      failed++;
      details.push({
        name,
        status: 'failed',
        reason: 'No phone number'
      });
      return;
    }

    const formattedPhone = formatPhoneForWhatsApp(phone);
    if (!formattedPhone) {
      failed++;
      details.push({
        name,
        status: 'failed',
        reason: 'Invalid phone number'
      });
      return;
    }

    // Open immediately in the same user gesture to avoid popup blocking
    const success = openWhatsApp(formattedPhone, message);
    if (success) {
      opened++;
      details.push({
        name,
        phone: formattedPhone,
        status: 'opened'
      });
    } else {
      failed++;
      details.push({
        name,
        status: 'failed',
        reason: 'Unable to open WhatsApp'
      });
    }
  });

  return { opened, failed, details };
};

